/**
 * Which Heritage branch a purchase order is sent from.
 *
 * Catalog stamps win: items downloaded via the branch catalog carry
 * catalog_branch_code, and when every line agrees the order ships from that
 * branch. But purchase-history downloads store no stamps by design — and
 * purchase history is the recommended download — so an order with no stamps
 * falls back to the branch the API already resolved from the vendor's
 * Heritage settings (or the company default). Blocking on missing stamps
 * made purchase-history-only companies unable to send at all.
 *
 * Genuinely mixed stamps still block: Heritage accepts one branch per order.
 */

type StampedItem = {
  catalog_branch_code?: string | null;
  catalog_branch_name?: string | null;
  /** #37 — an explicit per-line branch chosen on the review page. */
  line_branch_code?: string | null;
  line_branch_name?: string | null;
};

type FallbackBranch = {
  branch_code?: string | null;
  branch_name?: string | null;
} | null | undefined;

export type SendBranchSource = "catalog" | "vendor" | "company_default";

export type SendBranchResolution = {
  branchCode: string;
  branchName: string | null;
  source: SendBranchSource | null;
  status: "ready" | "mixed" | "unavailable";
};

export function resolveSendBranch(
  heritageItems: StampedItem[],
  fallbackBranch: FallbackBranch,
  fallbackSource: SendBranchSource | undefined
): SendBranchResolution {
  // A line with its own branch (#37) is already directed: the API places one
  // Heritage order per branch, so these lines neither need the catalog stamps
  // to agree nor participate in the uniformity check below.
  const undirected = heritageItems.filter((item) => !item.line_branch_code);

  if (undirected.length === 0 && heritageItems.length > 0) {
    // Every line carries its own branch; no base branch is needed at all.
    const sample = heritageItems[0];
    return {
      branchCode: String(sample.line_branch_code).trim(),
      branchName: sample.line_branch_name ?? null,
      source: "catalog",
      status: "ready",
    };
  }

  const withBranch = undirected.filter((item) => item.catalog_branch_code);

  if (withBranch.length > 0) {
    const uniqueCodes = [
      ...new Set(
        withBranch.map((item) => String(item.catalog_branch_code).trim().toUpperCase())
      ),
    ];

    const allItemsHaveSameBranch =
      withBranch.length === undirected.length && uniqueCodes.length === 1;

    if (!allItemsHaveSameBranch) {
      return { branchCode: "", branchName: null, source: null, status: "mixed" };
    }

    const sample = withBranch[0];
    return {
      branchCode: String(sample.catalog_branch_code).trim(),
      branchName: sample.catalog_branch_name ?? null,
      source: "catalog",
      status: "ready",
    };
  }

  // No stamps at all — the purchase-history case. Use the branch the API
  // resolved from vendor settings / company default.
  const fallbackCode = fallbackBranch?.branch_code?.trim();
  if (fallbackCode) {
    return {
      branchCode: fallbackCode,
      branchName: fallbackBranch?.branch_name ?? null,
      source: fallbackSource === "catalog" ? "vendor" : fallbackSource ?? "company_default",
      status: "ready",
    };
  }

  return { branchCode: "", branchName: null, source: null, status: "unavailable" };
}

/** One planned Heritage order: a branch and the lines it will carry. */
export type SendBranchGroup<T extends StampedItem = StampedItem> = {
  branchCode: string;
  branchName: string | null;
  items: T[];
};

/**
 * How the API will actually split this send (#37): one Heritage order per
 * distinct branch, lines without their own branch riding on the base branch
 * resolveSendBranch chose. Used to run the pre-send stock check per branch and
 * to tell the user how many orders they are about to place.
 */
export function groupSendBranches<T extends StampedItem>(
  heritageItems: T[],
  baseBranch: { branchCode: string; branchName: string | null }
): Array<SendBranchGroup<T>> {
  const groups = new Map<string, SendBranchGroup<T>>();
  for (const item of heritageItems) {
    const code = item.line_branch_code?.trim() || baseBranch.branchCode;
    const name = item.line_branch_code?.trim()
      ? item.line_branch_name ?? null
      : baseBranch.branchName;
    const key = code.toUpperCase();
    const group = groups.get(key);
    if (group) {
      group.items.push(item);
    } else {
      groups.set(key, { branchCode: code, branchName: name, items: [item] });
    }
  }
  return [...groups.values()];
}
